import { apiUrl } from "../utils/api";
import { apiFetch } from "../utils/apiFetch";

export type CleanupMode = "by-firma" | "by-person";
export type CleanupStatus = "open" | "bereinigt";
export type CleanupRole = "mensch" | "gf" | "gesellschafter";

export interface CleanupFirmaRow {
  companyId: number;
  companyName: string | null;
  statusLabel: string | null;
  relationCount: number;
  verantwortlich: string | null;
}
export interface CleanupPersonRow {
  personId: number;
  lastname: string | null;
  firstName: string | null;
  companyCount: number;
  companyNames: string[] | null;
  verantwortlich: string | null;
}

/** Kompakte, direkt mitgeladene Stammdaten zum Anzeigen in der Bereinigung. */
export interface CleanupContactInfo {
  birthDate?: string | null;
  gender?: string | null;
  birthPlace?: string | null;
  nationality?: string | null;
  maritalStatus?: string | null;
  callName?: string | null;
  jobStatus?: string | null;
  jobOccupation?: string | null;
  jobIndustry?: string | null;
  jobEmployer?: string | null;
  erreichbarkeit?: string | null;
  remark?: string | null;
  salutationLabel?: string | null;
  titleLabel?: string | null;
  address?: { street?: string; postalCode?: string; city?: string } | null;
  communications?: Array<{ type: string; value: string; isDefault: boolean }>;
}

/** A person row inside a company detail (one Firma↔Person relation). */
export interface CleanupPersonDetail extends CleanupContactInfo {
  relationId: number;
  personId: number;
  lastname: string | null;
  firstName: string | null;
  currentRelationType: string | null;
  currentFunction: string | null;
  currentIsExtern: boolean | null;
  currentShare: string | null;
  currentExternalCompanyId: number | null;
  currentExternalCompanyName: string | null;
  statusId: number | null;
  statusLabel: string | null;
  /** Zusätzliche (Multi-)Status aus ContactStatusLink, z. B. „Mensch im Unternehmen". */
  extraStatuses?: string[];
  bereinigtAt: string | null;
}
export interface FirmaDetail {
  firma: {
    id: number;
    name: string | null;
    statusLabel: string | null;
    address: { street?: string; postalCode?: string; city?: string } | null;
    communications: Array<{ type: string; value: string; isDefault: boolean }>;
  };
  persons: CleanupPersonDetail[];
}
/** A company a person is linked to (one Firma↔Person relation). */
export interface CleanupCompanyRef {
  relationId: number;
  companyId: number;
  companyName: string | null;
  currentRelationType: string | null;
  currentFunction: string | null;
  currentIsExtern: boolean | null;
  currentShare: string | null;
  currentExternalCompanyId: number | null;
  currentExternalCompanyName: string | null;
  bereinigtAt: string | null;
}
export interface PersonDetail {
  person: CleanupContactInfo & {
    id: number;
    lastname: string | null;
    firstName: string | null;
    statusId: number | null;
    statusLabel: string | null;
    /** Zusätzliche (Multi-)Status aus ContactStatusLink. */
    extraStatuses?: string[];
  };
  companies: CleanupCompanyRef[];
}

export interface PersonCleanupInput {
  relationId: number;
  personId: number;
  companyId: number;
  /** Optional: bei bereinigtOnly / reiner Mensch-im-Unternehmen-Markierung keine Rolle. */
  role?: CleanupRole;
  isMitarbeiterBav: boolean;
  /** Additiv den Kontakt-Status „Mensch im Unternehmen" setzen (+ bereinigt). */
  isMenschImUnternehmen?: boolean;
  /** Beziehung nur als bereinigt markieren (keine Rolle, kein Status). */
  bereinigtOnly?: boolean;
  /** Firma↔Person-Beziehung hart löschen (keine Verantwortlichkeit fürs Unternehmen). */
  deleteRelation?: boolean;
  /** „Mensch fürs Unternehmen": intern (false) / extern (true). */
  isExtern?: boolean;
  /** „Mensch fürs Unternehmen": konkrete Verantwortlichkeit/Rolle (z. B. „Steuerberater"). */
  verantwortlichkeit?: string;
  /** „Mensch fürs Unternehmen" + extern: bereits bestehende externe Firma (Contact-Id). */
  externalCompanyId?: number;
  /** „Mensch fürs Unternehmen" + extern: neue, minimal anzulegende externe Firma. */
  externalCompanyNew?: { name: string; phone?: string; email?: string; web?: string; branche?: string; legalFormId?: number };
  /** „Gesellschafter": Anteile (Freitext). */
  share?: string;
  /** „Gesellschafter": ist zugleich Geschäftsführer. */
  istGeschaeftsfuehrer?: boolean;
  /** Mehrfach-Rollen: erste Rolle aktualisiert relationId; weitere (isNew) legen neue Relation an. */
  isNew?: boolean;
  /** #4: neu hinzugefügte Person (Mensch fürs Unternehmen) — bestehender Kontakt
   *  (contactId) ODER frei eingetippt; wird beim Speichern aufgelöst/angelegt. */
  newPerson?: {
    contactId?: number;
    lastName?: string;
    firstName?: string;
    gender?: string;
    salutationId?: number;
    salutationLabel?: string;
    birthDate?: string;
    phonePrivate?: string;
    phoneWork?: string;
    mobilePrivate?: string;
    mobileWork?: string;
    emailPrivate?: string;
    emailWork?: string;
  };
}

/** Work list (by-firma rows or by-person rows) + total for the header. */
export const listCleanup = async (
  mode: CleanupMode,
  status: CleanupStatus,
  teamId: string | undefined,
  q: string | undefined,
  limit: number,
  offset: number,
  signal?: AbortSignal,
): Promise<{ rows: any[]; total: number }> => {
  const { data, total } = await apiFetch<any[]>("ansprechpartner-cleanup", {
    params: {
      mode,
      status,
      ...(teamId ? { teamId } : {}),
      ...(q ? { q } : {}),
      limit,
      offset,
    },
    signal,
  });
  return { rows: data ?? [], total: total ?? data?.length ?? 0 };
};

export const listCleanupTeams = async (): Promise<
  Array<{ id: string; name: string | null }>
> => {
  const { data } = await apiFetch<Array<{ id: string; name: string | null }>>(
    "ansprechpartner-cleanup/teams",
  );
  return data ?? [];
};

export const fetchCleanupFirma = async (id: number): Promise<FirmaDetail> => {
  const { data } = await apiFetch<FirmaDetail>(
    `ansprechpartner-cleanup/firma/${id}`,
  );
  return data;
};

export const fetchCleanupPerson = async (id: number): Promise<PersonDetail> => {
  const { data } = await apiFetch<PersonDetail>(
    `ansprechpartner-cleanup/person/${id}`,
  );
  return data;
};

/** Eine Firma aus der Kundenverbindung der Person (für zusätzliche Verantwortlichkeiten). */
export interface KvCompany {
  id: number;
  name: string | null;
  statusLabel: string | null;
}

/**
 * Firma-Mitglieder der Kundenverbindung(en), in denen die Person ist — ohne die
 * aktuelle Firma (`excludeCompanyId`). Für „Verantwortlichkeit direkt auch für
 * andere KV-Firmen vergeben".
 */
export const fetchKvCompaniesForPerson = async (
  personId: number,
  excludeCompanyId?: number,
): Promise<KvCompany[]> => {
  const { data } = await apiFetch<KvCompany[]>(
    `ansprechpartner-cleanup/person/${personId}/kv-companies`,
    { params: excludeCompanyId ? { excludeCompanyId } : {} },
  );
  return data ?? [];
};

export const saveCleanupBatch = async (
  items: PersonCleanupInput[],
): Promise<{ updated: number }> => {
  const res = await fetch(apiUrl("ansprechpartner-cleanup/save"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items }),
  });
  if (!res.ok) {
    let msg = `Speichern fehlgeschlagen: HTTP ${res.status}`;
    try {
      const d = await res.json();
      if (d?.message)
        msg = Array.isArray(d.message) ? d.message.join(", ") : String(d.message);
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return res.json();
};

/**
 * Verschiebt Kontakte AUS DER BEREINIGUNG in den Papierkorb (Scope contacts:update,
 * nicht der admin-gegatete DELETE /contact) + löscht ihre Firma-Verknüpfungen.
 */
export const deleteCleanupContacts = async (
  personIds: number[],
  reason?: string,
): Promise<void> => {
  const ids = (personIds ?? []).filter((x) => Number.isFinite(x));
  if (!ids.length) return;
  const res = await fetch(apiUrl("ansprechpartner-cleanup/delete-contacts"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ personIds: ids, reason }),
  });
  if (!res.ok) {
    let msg = `Löschen fehlgeschlagen: HTTP ${res.status}`;
    try {
      const d = await res.json();
      if (d?.message) msg = Array.isArray(d.message) ? d.message.join(", ") : String(d.message);
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
};
